import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// CSV: currency_code,rate_date,gbp_rate
// gbp_rate = units of currency per 1 GBP
export default function FxRatesUploader() {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const { toast } = useToast();

  const loadStats = async () => {
    const { count } = await supabase.from("policy_fx_rates" as any).select("*", { count: "exact", head: true });
    setCount(count ?? 0);
  };
  useEffect(() => { loadStats(); }, []);

  const upload = async () => {
    setBusy(true);
    try {
      const lines = csv.trim().split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) throw new Error("No rows.");
      const data = lines[0].toLowerCase().includes("currency") ? lines.slice(1) : lines;

      const rows = data.map(l => {
        const [ccy, date, rateRaw] = l.split(",").map(s => s.trim());
        const gbp_rate = Number(rateRaw);
        if (!ccy || !date || !Number.isFinite(gbp_rate)) throw new Error(`Bad row: ${l}`);
        return { currency_code: ccy.toUpperCase(), rate_date: date, gbp_rate };
      });

      const chunkSize = 500;
      let total = 0;
      for (let i = 0; i < rows.length; i += chunkSize) {
        const chunk = rows.slice(i, i + chunkSize);
        const { error } = await supabase.from("policy_fx_rates" as any).upsert(chunk, { onConflict: "currency_code,rate_date" });
        if (error) throw error;
        total += chunk.length;
      }
      toast({ title: "Upload complete", description: `${total} rates upserted.` });
      setCsv("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>FX Rates (GBP base)</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Daily cross rates expressed as <strong>units of foreign currency per 1 GBP</strong>. Used to normalize non-GBP holdings into GBP.</p>
          <p><strong>CSV:</strong> <code>currency_code,rate_date,gbp_rate</code></p>
        </div>
        <div className="text-xs border rounded p-3 bg-muted/30">
          <span className="text-muted-foreground">Rate rows:</span>{" "}
          <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Paste CSV</Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"currency_code,rate_date,gbp_rate\nUSD,2026-05-15,1.2734\nEUR,2026-05-15,1.1685"}
            rows={8}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={upload} disabled={busy || !csv.trim()}>{busy ? "Uploading…" : "Upload"}</Button>
          <Button variant="outline" onClick={() => setCsv("")} disabled={busy}>Clear</Button>
        </div>
      </CardContent>
    </Card>
  );
}
